import { type SupportArticle } from "@src/appV2/HelpCenter/types";

import { ALL_SUPPORT_ARTICLES_CATEGORY } from "./constants";
import { type SupportArticleGroup } from "./types";

interface GetFilteredSupportArticleGroupsParams {
  supportArticles: SupportArticle[];
  searchQuery: string;
  selectedCategory: string;
}

interface GetSuggestedSupportArticlesParams {
  supportArticles: SupportArticle[];
  searchQuery: string;
  limit: number;
}

export function getSupportArticleCategories(supportArticles: SupportArticle[]): string[] {
  const categories = Array.from(
    new Set(supportArticles.map((supportArticle) => supportArticle.attributes.category))
  ).toSorted((leftCategory, rightCategory) => leftCategory.localeCompare(rightCategory));

  return [ALL_SUPPORT_ARTICLES_CATEGORY, ...categories];
}

export function getFilteredSupportArticleGroups(
  params: GetFilteredSupportArticleGroupsParams
): SupportArticleGroup[] {
  const { supportArticles, searchQuery, selectedCategory } = params;
  const normalizedSearchQuery = normalizeSearchQuery(searchQuery);

  const filteredSupportArticles = supportArticles.filter(
    (supportArticle) =>
      matchesSelectedCategory(supportArticle, selectedCategory) &&
      matchesSearchQuery(supportArticle, normalizedSearchQuery)
  );

  const groupedSupportArticles: Record<string, SupportArticle[]> = {};

  for (const supportArticle of filteredSupportArticles) {
    const { category } = supportArticle.attributes;
    const group = groupedSupportArticles[category];

    if (group) {
      group.push(supportArticle);
    } else {
      groupedSupportArticles[category] = [supportArticle];
    }
  }

  return Object.entries(groupedSupportArticles)
    .map(([category, groupedArticles]) => ({
      category,
      supportArticles: groupedArticles.toSorted((leftArticle, rightArticle) =>
        leftArticle.attributes.title.localeCompare(rightArticle.attributes.title)
      ),
    }))
    .toSorted((leftGroup, rightGroup) => leftGroup.category.localeCompare(rightGroup.category));
}

export function getSuggestedSupportArticles(
  params: GetSuggestedSupportArticlesParams
): SupportArticle[] {
  const { supportArticles, searchQuery, limit } = params;
  const normalizedSearchQuery = normalizeSearchQuery(searchQuery);

  if (normalizedSearchQuery.length === 0) {
    return [];
  }

  return supportArticles
    .map((supportArticle) => ({
      supportArticle,
      score: getSupportArticleSuggestionScore({
        supportArticle,
        normalizedSearchQuery,
      }),
    }))
    .filter((scoredSupportArticle) => scoredSupportArticle.score > 0)
    .toSorted((leftArticle, rightArticle) => {
      if (leftArticle.score !== rightArticle.score) {
        return rightArticle.score - leftArticle.score;
      }

      return leftArticle.supportArticle.attributes.title.localeCompare(
        rightArticle.supportArticle.attributes.title
      );
    })
    .slice(0, limit)
    .map((scoredSupportArticle) => scoredSupportArticle.supportArticle);
}

function normalizeSearchQuery(searchQuery: string): string {
  return searchQuery.trim().toLowerCase();
}

function matchesSearchQuery(supportArticle: SupportArticle, searchQuery: string): boolean {
  if (searchQuery.length === 0) {
    return true;
  }

  return (
    supportArticle.attributes.title.toLowerCase().includes(searchQuery) ||
    supportArticle.attributes.category.toLowerCase().includes(searchQuery)
  );
}

function matchesSelectedCategory(
  supportArticle: SupportArticle,
  selectedCategory: string
): boolean {
  return (
    selectedCategory === ALL_SUPPORT_ARTICLES_CATEGORY ||
    supportArticle.attributes.category === selectedCategory
  );
}

function getSupportArticleSuggestionScore(params: {
  supportArticle: SupportArticle;
  normalizedSearchQuery: string;
}): number {
  const { supportArticle, normalizedSearchQuery } = params;
  const normalizedTitle = normalizeSearchQuery(supportArticle.attributes.title);
  const normalizedCategory = normalizeSearchQuery(supportArticle.attributes.category);
  const normalizedTitleWords = normalizedTitle.split(" ");

  let score = 0;

  if (normalizedTitle === normalizedSearchQuery) {
    score += 120;
  }

  if (normalizedTitle.startsWith(normalizedSearchQuery)) {
    score += 80;
  }

  if (normalizedTitle.includes(normalizedSearchQuery)) {
    score += 50;
  }

  if (normalizedTitleWords.some((titleWord) => titleWord.startsWith(normalizedSearchQuery))) {
    score += 30;
  }

  if (normalizedCategory.startsWith(normalizedSearchQuery)) {
    score += 20;
  }

  if (normalizedCategory.includes(normalizedSearchQuery)) {
    score += 10;
  }

  return score;
}
